import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { DateTime as LuxonDateTime } from 'luxon';

import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { Workflows } from 'src/providers/workflows/workflows';
import { AgentsProvider } from 'src/providers/agents/agents';
import { WorkflowRunPage } from 'src/pages/workflows/workflow-run/workflow-run';

import { ActivityRow, ActivitySummary, toActivityRows, toActivitySummary } from 'src/shared/components/agent-activity-list/activity-row';
import { SelectModel } from 'src/tidy-ui-components/components/form/select/select.model';

@Component({
  selector: 'agent-activity',
  standalone: false,
  template: `
    @if (!isMessageAgent) {
      <tidy-agent-activity-list
        [rows]="rows"
        [loading]="loading"
        [loadingMore]="loadingMore"
        [agents]="agents"
        [filterName]="filterName"
        [summary]="summary"
        [canLoadMore]="canLoadMore"
        (filterChange)="filterByWorkflowId($event)"
        (clearFilter)="clearFilter()"
        (loadMore)="loadMoreActivity()"
        (openRun)="goToWorkflowRun($event)">
        <tidy-header-datepicker
          activity-date-filter
          class="align-dropdown-right"
          (stringDateOption)="selectStringDateRange($event)"
          (customDateOption)="selectCustomDateRange($event)"
          [(dateRangeForm)]="dateRangeForm"
          [selectOptions]="selectOptions"
          [header]="true" />
      </tidy-agent-activity-list>
    }
    @if (isMessageAgent) {
      <div class="activity-list">
        @for (item of activityRuns; track item) {
          <div class="activity-item">
            <div class="activity-item-content">
              <tidy-text [body]="item.text" />
              <tidy-text [helper]="item.created_at | parseDateTime:'MMM d h:mma':'local'" />
            </div>
          </div>
        }
        @if (activityRuns.length === 0) {
          <div class="activity-empty">
            <tidy-text [helper]="'No activity yet.'" />
          </div>
        }
      </div>
    }
  `,
  styles: [`
    :host {
      display: flex;
      flex-direction: column;
      min-height: 100%;
    }
    .activity-list {
      flex: 1;
      overflow-y: auto;
      padding: 10px 15px;
    }
    .activity-item {
      padding: 12px 10px;
      border-bottom: 1px solid var(--tidy-border-color);
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    .activity-item:last-child {
      border-bottom: none;
    }
    .activity-item-content {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    .activity-empty {
      padding: 20px;
      text-align: center;
    }
  `]
})
export class AgentActivityPage implements OnInit {

  @Input() data: any;

  activityRuns: any[] = [];
  rows: ActivityRow[] = [];
  workflowIds: number[] = [];
  agents: SelectModel[] = [];
  filterWorkflowId: number | null = null;
  filterName = '';
  summary: ActivitySummary | null = null;
  totalRecords = 0;
  failedRecords: number | null = null;
  loading = false;
  loadingMore = false;
  selectOptions: any[] = [];
  dateRangeForm: UntypedFormGroup;
  startDate: string;
  endDate: string;
  isMessageAgent = false;
  agentKey = '';

  private page = 1;
  private requestSeq = 0;
  private readonly perPage = 50;

  constructor(
    private rightSidePanelService: RightSidePanelService,
    private workflowsProvider: Workflows,
    private agentsProvider: AgentsProvider,
    private fb: UntypedFormBuilder,
  ) {}

  get canLoadMore(): boolean {
    return !this.loading && this.activityRuns.length < this.totalRecords;
  }

  ngOnInit() {
    this.rightSidePanelService.setDialogPageTitle('Activity');
    this.activityRuns = this.data?.activityRuns || [];
    this.workflowIds = (this.data?.workflowIds || []).map(Number);
    this.agents = this.data?.agents || [];
    this.filterWorkflowId = this.data?.filterWorkflowId ?? null;
    this.filterName = this.data?.filterName || '';
    this.totalRecords = Number(this.data?.totalRecords) || this.activityRuns.length;
    this.failedRecords = this.data?.failedRecords ?? null;
    this.page = Number(this.data?.page) || 1;
    this.selectOptions = this.data?.activitySelectOptions || [];
    this.isMessageAgent = this.data?.isMessageAgent || false;
    this.agentKey = this.data?.agentKey || '';
    this.dateRangeForm = this.fb.group({
      start: '',
      end: '',
      customStringSelect: 'all_time'
    });
    this.updateRows();
  }

  async selectStringDateRange(selection: string) {
    const today = LuxonDateTime.local();
    this.endDate = today.toISODate();
    if (selection === 'last_week') {
      this.startDate = today.minus({ days: 7 }).toISODate();
    } else if (selection === 'last_month') {
      this.startDate = today.minus({ months: 1 }).toISODate();
    } else if (selection === 'all_time') {
      this.startDate = '2023-8-10';
    } else if (selection === 'today') {
      this.startDate = today.toISODate();
    }
    await this.loadActivity();
  }

  async selectCustomDateRange(selection: { start: string; end: string }) {
    const start = new Date(selection.start);
    const end = new Date(selection.end);
    this.startDate = LuxonDateTime.fromJSDate(start).toISODate();
    this.endDate = LuxonDateTime.fromJSDate(end).toISODate();
    await this.loadActivity();
  }

  filterByWorkflowId(workflowId: string) {
    const option = this.agents.find(o => o.value === workflowId);
    if (!option) {
      return;
    }
    this.filterWorkflowId = Number(option.value);
    this.filterName = option.viewValue;
    this.loadActivity();
  }

  clearFilter() {
    if (this.filterWorkflowId === null) {
      return;
    }
    this.filterWorkflowId = null;
    this.filterName = '';
    this.loadActivity();
  }

  loadMoreActivity() {
    if (this.loading || this.loadingMore || !this.canLoadMore) {
      return;
    }
    this.loadActivity(true);
  }

  /** Every agent on the account, narrowed to one while a filter is set. */
  private activeWorkflowIds(): number[] {
    if (this.filterWorkflowId !== null) {
      return [this.filterWorkflowId];
    }
    return this.workflowIds;
  }

  private async loadActivity(append = false) {
    if (this.isMessageAgent) {
      try {
        this.activityRuns = await this.agentsProvider.getAgentActivity(this.agentKey, 1, 20);
      } catch {
        this.activityRuns = [];
      }
      return;
    }

    const workflowIds = this.activeWorkflowIds();
    if (!workflowIds.length) {
      this.activityRuns = [];
      this.totalRecords = 0;
      this.failedRecords = null;
      this.updateRows();
      return;
    }

    const seq = ++this.requestSeq;
    const page = append ? this.page + 1 : 1;
    if (append) {
      this.loadingMore = true;
    } else {
      this.loading = true;
    }
    try {
      const today = LuxonDateTime.local();
      const startDate = this.startDate || '2023-8-10';
      const endDate = this.endDate || today.toISODate();
      const [response, failedResponse] = await Promise.all([
        this.workflowsProvider.getWorkflowVersionRuns(workflowIds, startDate, endDate, page, this.perPage),
        append ? null : this.workflowsProvider.getWorkflowVersionRuns(workflowIds, startDate, endDate, 1, 1, null, 'failed'),
      ]);
      if (seq !== this.requestSeq) {
        return;
      }
      const runs: any[] = response.body || [];
      const seen = new Set(this.activityRuns.map(r => r.id));
      this.activityRuns = append ? [...this.activityRuns, ...runs.filter(r => !seen.has(r.id))] : runs;
      this.totalRecords = Number(response.totalRecords) || this.activityRuns.length;
      if (failedResponse) {
        const failed = Number(failedResponse.totalRecords);
        this.failedRecords = isNaN(failed) ? null : failed;
      }
      this.page = page;
    } catch {
      if (seq !== this.requestSeq) {
        return;
      }
      if (!append) {
        this.activityRuns = [];
        this.totalRecords = 0;
        this.failedRecords = null;
        this.page = 1;
      }
    }
    this.loading = false;
    this.loadingMore = false;
    this.updateRows();
  }

  private updateRows() {
    this.rows = toActivityRows(this.activityRuns);
    this.summary = toActivitySummary(this.rows, this.totalRecords, this.failedRecords);
  }

  goToWorkflowRun(run: any) {
    const params = { run, selectedWorkflow: '' };
    this.rightSidePanelService.navigateTo(
      'automations/workflow-history/workflow-run', params, WorkflowRunPage
    );
  }
}
